import type { EventAiTagVariant, EventAlarmRow } from '../../models/entities/event-detail.model';

/** Historic alarms have no end time — paint a fixed 10s block from start. */
export const HISTORIC_ALARM_DURATION_MS = 10_000;

export interface HistoricPlaybackAlarmSegment {
  start: Date;
  end: Date;
}

export interface HistoricPlaybackAlarmSegments {
  motion: HistoricPlaybackAlarmSegment[];
  human: HistoricPlaybackAlarmSegment[];
  vehicle: HistoricPlaybackAlarmSegment[];
  gun: HistoricPlaybackAlarmSegment[];
}

export interface HistoricPlaybackAlarmsResult {
  alarms: EventAlarmRow[];
  segments: HistoricPlaybackAlarmSegments;
}

const TIMELINE_VARIANTS: readonly EventAiTagVariant[] = ['motion', 'human', 'vehicle', 'gun'];

export function emptyHistoricPlaybackAlarmSegments(): HistoricPlaybackAlarmSegments {
  return {
    motion: [],
    human: [],
    vehicle: [],
    gun: [],
  };
}

function asNonBlankId(value: unknown): string | null {
  if (value == null) {
    return null;
  }
  const eventId = String(value).trim();
  return eventId.length ? eventId : null;
}

function resolveEventId(item: unknown): string | null {
  if (typeof item === 'string' || typeof item === 'number') {
    return asNonBlankId(item);
  }
  if (!item || typeof item !== 'object') {
    return null;
  }
  const row = item as Record<string, unknown>;
  return asNonBlankId(row['eventId'] ?? row['EventId'] ?? row['eventID'] ?? row['id'] ?? row['Id']);
}

function extractHistoryItems(payload: unknown): unknown[] {
  if (Array.isArray(payload)) {
    return payload;
  }
  if (!payload || typeof payload !== 'object') {
    return [];
  }
  const body = payload as Record<string, unknown>;
  if (Array.isArray(body['items'])) {
    return body['items'];
  }
  if (Array.isArray(body['events'])) {
    return body['events'];
  }
  if (Array.isArray(body['data'])) {
    return body['data'];
  }
  return [];
}

export function mergeHistoricEventIds(
  historyEventIds: readonly string[],
  extraEventIds: readonly string[] = [],
): string[] {
  const eventIds = [...historyEventIds];
  const seen = new Set(eventIds);
  for (const extra of extraEventIds) {
    const eventId = extra.trim();
    if (eventId && !seen.has(eventId)) {
      seen.add(eventId);
      eventIds.push(eventId);
    }
  }
  return eventIds;
}

/** Collects usable event ids from Resolve `GET /sites/{siteId}/event-history`. */
export function mapSiteEventHistoryToEventIds(payload: unknown): string[] {
  const eventIds: string[] = [];
  const seen = new Set<string>();
  for (const item of extractHistoryItems(payload)) {
    const eventId = resolveEventId(item);
    if (eventId && !seen.has(eventId)) {
      seen.add(eventId);
      eventIds.push(eventId);
    }
  }
  return eventIds;
}

function resolveAlarmTimelineVariants(alarm: EventAlarmRow): EventAiTagVariant[] {
  const variants = alarm.aiTags
    .map((tag) => tag.variant)
    .filter((variant): variant is EventAiTagVariant => TIMELINE_VARIANTS.includes(variant));
  return variants.length ? variants : ['motion'];
}

function resolveTimelineVariant(
  alarm: EventAlarmRow,
  selectedTypes?: readonly EventAiTagVariant[],
): EventAiTagVariant | null {
  const alarmTypes = resolveAlarmTimelineVariants(alarm);
  if (selectedTypes == null) {
    return alarmTypes[0] ?? 'motion';
  }
  if (!selectedTypes.length) {
    return null;
  }
  const selected = new Set(selectedTypes);
  return TIMELINE_VARIANTS.find((variant) => selected.has(variant) && alarmTypes.includes(variant)) ?? null;
}

function resolveAlarmCameraKey(cameraId: string): string {
  const numeric = Number(cameraId);
  return Number.isFinite(numeric) ? String(numeric) : cameraId;
}

/** Maps mapped Event View alarms onto Caliber AI timeline keys for one camera. */
export function mapHistoricAlarmsToTimelineSegments(
  alarms: readonly EventAlarmRow[],
  cameraNumericId: number,
  selectedTypes?: readonly EventAiTagVariant[],
): HistoricPlaybackAlarmSegments {
  const cameraKey = String(cameraNumericId);
  const segments = emptyHistoricPlaybackAlarmSegments();

  for (const alarm of alarms) {
    if (resolveAlarmCameraKey(alarm.cameraId) !== cameraKey) {
      continue;
    }
    const startMs = Date.parse(alarm.timestamp);
    if (!Number.isFinite(startMs)) {
      continue;
    }
    const variant = resolveTimelineVariant(alarm, selectedTypes);
    if (!variant) {
      continue;
    }
    segments[variant].push({
      start: new Date(startMs),
      end: new Date(startMs + HISTORIC_ALARM_DURATION_MS),
    });
  }

  return segments;
}
